import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "operationType",
    "appNameSelect",
    "fileNameSelect",
    "addSection",
    "removeSection",
    "addSecretsTable",
    "removeKeysTable"
  ]

  static values = {
    initialAppName: String,
    initialFileName: String
  }

  connect() {
    console.log("SecretChanges controller connected")
    this.loadAppNames()
    this.showOperationSection()
  }

  // Handle operation type radio button changes
  operationTypeChanged(event) {
    this.showOperationSection()
  }

  // Show the appropriate section based on selected operation
  showOperationSection() {
    const selectedOperation = this.getSelectedOperation()

    // Re-enable all inputs first
    this.enableAllInputs()

    // Hide all sections first
    this.addSectionTarget.style.display = 'none'
    this.removeSectionTarget.style.display = 'none'

    // Show the selected section
    switch(selectedOperation) {
      case 'upserted':
        this.addSectionTarget.style.display = 'block'
        break
      case 'removed':
        this.removeSectionTarget.style.display = 'block'
        break
    }
  }

  // Get the currently selected operation type
  getSelectedOperation() {
    const checkedRadio = this.operationTypeTargets.find(radio => radio.checked)
    return checkedRadio ? checkedRadio.value : 'upserted'
  }

  // Load distinct app names from the server
  async loadAppNames() {
    try {
      const response = await fetch('/api/v1/secrets/app_names', {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (response.ok) {
        const appNames = await response.json()
        this.populateAppNameSelects(appNames)
      }
    } catch (error) {
      console.error('Failed to load app names:', error)
    }
  }

  // Populate all app name select boxes
  populateAppNameSelects(appNames) {
    this.appNameSelectTargets.forEach(select => {
      // Clear existing options except prompt
      const promptOption = select.querySelector('option[value=""]')
      select.innerHTML = ''
      if (promptOption) {
        select.appendChild(promptOption)
      } else {
        select.innerHTML = '<option value="">Select an app...</option>'
      }

      appNames.forEach(appName => {
        const option = document.createElement('option')
        option.value = appName
        option.textContent = appName
        // Set selected if this matches initial value
        if (this.initialAppNameValue && appName === this.initialAppNameValue) {
          option.selected = true
        }
        select.appendChild(option)
      })

      // If we set an initial value, trigger key loading
      if (this.initialAppNameValue && select.value === this.initialAppNameValue) {
        this.loadFileNames(this.initialAppNameValue, this.getCorrespondingFileNameSelect(select))
      }
    })
  }

  // Handle app name selection change
  async appNameChanged(event) {
    const appName = event.target.value
    const fileNameSelect = this.getCorrespondingFileNameSelect(event.target)

    if (appName) {
      await this.loadFileNames(appName, fileNameSelect)
    } else {
      fileNameSelect.innerHTML = '<option value="">Select a file...</option>'
    }
  }

    // Get the corresponding file name select for an app name select
  getCorrespondingFileNameSelect(appNameSelect) {
    // Since we now have shared selects, always return the first (and only) file name select
    return this.fileNameSelectTargets[0]
  }

  // Load file names for a specific app
  async loadFileNames(appName, fileNameSelect) {
    try {
      const response = await fetch(`/api/v1/secrets/file_names?app_name=${encodeURIComponent(appName)}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (response.ok) {
        const fileNames = await response.json()
        this.populateFileNameSelect(fileNameSelect, fileNames)
      }
    } catch (error) {
      console.error('Failed to load file names:', error)
    }
  }

  // Populate a file name select box
  populateFileNameSelect(select, fileNames) {
    // Clear existing options except prompt
    const promptOption = select.querySelector('option[value=""]')
    select.innerHTML = ''
    if (promptOption) {
      select.appendChild(promptOption)
    } else {
      select.innerHTML = '<option value="">Select a file...</option>'
    }

    fileNames.forEach(fileName => {
      const option = document.createElement('option')
      option.value = fileName
      option.textContent = fileName
      // Set selected if this matches initial value
      if (this.initialFileNameValue && fileName === this.initialFileNameValue) {
        option.selected = true
      }
      select.appendChild(option)
    })
  }

  // Add a new row to the secrets table (for add and update operations)
  addSecretRow(event) {
    event.preventDefault()

    const button = event.target
    const table = this.addSecretsTableTarget
    const tbody = table.querySelector('tbody')

    const newRow = this.createSecretRow()
    tbody.appendChild(newRow)
  }

  // Create a new secret row
  createSecretRow() {
    const row = document.createElement('tr')
    row.innerHTML = `
      <td>
        <input type="text" name="secret_change[payload][][name]" class="form-control" placeholder="Secret name" autocomplete="off">
      </td>
      <td>
        <input type="text" name="secret_change[payload][][value]" class="form-control" placeholder="Secret value" autocomplete="off">
      </td>
      <td>
        <button type="button" class="btn btn-outline-danger btn-sm" data-action="click->secret-changes#removeSecretRow">
          <i class="fas fa-trash"></i>
        </button>
      </td>
    `
    return row
  }

  // Remove a secret row
  removeSecretRow(event) {
    event.preventDefault()
    const button = event.target.closest('button')
    const row = button.closest('tr')
    row.remove()
  }

  // Add a new row to the remove keys table
  addRemoveKeyRow(event) {
    event.preventDefault()

    const table = this.removeKeysTableTarget
    const tbody = table.querySelector('tbody')

    const newRow = this.createRemoveKeyRow()
    tbody.appendChild(newRow)
  }

  // Create a new remove key row
  createRemoveKeyRow() {
    const row = document.createElement('tr')
    row.innerHTML = `
      <td>
        <input type="text" name="secret_change[payload][][name]" class="form-control" placeholder="Key name to remove" autocomplete="off">
      </td>
      <td>
        <button type="button" class="btn btn-outline-danger btn-sm" data-action="click->secret-changes#removeKeyRow">
          <i class="fas fa-trash"></i>
        </button>
      </td>
    `
    return row
  }

  // Remove a key row from the remove keys table
  removeKeyRow(event) {
    event.preventDefault()
    const button = event.target.closest('button')
    const row = button.closest('tr')
    row.remove()
  }

  // Handle form submission
  submitForm(event) {
    event.preventDefault()

    // Get the actual form element
    const form = event.target.closest('form')
    if (!form) {
      console.error('No form found')
      return
    }

    const operation = this.getSelectedOperation()
    console.log('Submitting form for operation:', operation)

    // Disable inputs in hidden sections to prevent conflicts
    this.disableHiddenSectionInputs(operation)

    // Validate the form has required data
    if (!this.validateForm(operation)) {
      return
    }

    // For upserted operations, we need to restructure the payload data
    if (operation === 'upserted') {
      this.prepareUpsertedPayload(form)
    }

    // Let the form submit normally - Rails will handle the nested parameters correctly
    form.submit()
  }

  // Validate form data before submission
  validateForm(operation) {
    // Check shared app_name select (required for both operations)
    const appNameSelect = this.appNameSelectTargets[0] // Now there's only one
    if (!appNameSelect || !appNameSelect.value.trim()) {
      alert('Please select an application name.')
      return false
    }

    // File name select is only required for upserted operations
    if (operation === 'upserted') {
      const fileNameSelect = this.fileNameSelectTargets[0] // Now there's only one
      if (!fileNameSelect || !fileNameSelect.value.trim()) {
        alert('Please select a file name.')
        return false
      }
    }

    if (operation === 'upserted') {

      const table = this.addSecretsTableTarget
      const rows = table.querySelectorAll('tbody tr:not([style*="display: none"])')
      let hasValidSecrets = false

      rows.forEach(row => {
        const nameInput = row.querySelector('input[name="secret_change[payload][][name]"]')
        const valueInput = row.querySelector('input[name="secret_change[payload][][value]"]')

        if (nameInput && valueInput && nameInput.value.trim() && valueInput.value.trim()) {
          hasValidSecrets = true
        }
      })

      if (!hasValidSecrets) {
        alert('Please add at least one secret with both name and value.')
        return false
      }
    } else if (operation === 'removed') {
      // For remove operations, validate that at least one key name is specified
      const table = this.removeKeysTableTarget
      const rows = table.querySelectorAll('tbody tr:not([style*="display: none"])')
      let hasValidKeys = false

      rows.forEach(row => {
        const nameInput = row.querySelector('input[name="secret_change[payload][][name]"]')
        if (nameInput && nameInput.value.trim()) {
          hasValidKeys = true
        }
      })

      if (!hasValidKeys) {
        alert('Please specify at least one key name to remove.')
        return false
      }
    }    return true
  }

  // Prepare upserted payload data in the format Rails expects
  prepareUpsertedPayload(form) {
    // Remove any existing payload inputs to avoid duplicates
    const existingInputs = form.querySelectorAll('input[name^="secret_change[payload]"]')
    existingInputs.forEach(input => {
      if (input.closest('table')) {
        // Don't remove inputs that are in the visible table
        return
      }
      input.remove()
    })

    // Collect secrets from the table
    const secrets = this.collectSecretsFromTable(this.addSecretsTableTarget)

    // Create hidden inputs in the format Rails expects: secret_change[payload][][name] and secret_change[payload][][value]
    secrets.forEach((secret, index) => {
      // Create hidden inputs for each secret
      const nameInput = document.createElement('input')
      nameInput.type = 'hidden'
      nameInput.name = `secret_change[payload][${index}][name]`
      nameInput.value = secret.name
      form.appendChild(nameInput)

      const valueInput = document.createElement('input')
      valueInput.type = 'hidden'
      valueInput.name = `secret_change[payload][${index}][value]`
      valueInput.value = secret.value
      form.appendChild(valueInput)
    })

    // Remove the table inputs to avoid conflicts
    const tableInputs = form.querySelectorAll('input[name="secret_change[payload][][name]"], input[name="secret_change[payload][][value]"]')
    tableInputs.forEach(input => {
      input.name = '' // Clear the name to prevent submission
    })

    console.log(`Prepared ${secrets.length} secrets for submission`)
  }

  // Debug method to check payload collection (for troubleshooting)
  debugPayloadCollection() {
    const operation = this.getSelectedOperation()
    console.log('Current operation:', operation)

    if (operation === 'upserted') {
      const secrets = this.collectSecretsFromTable(this.addSecretsTableTarget)
      console.log('Collected secrets:', secrets)
    }
  }  // Collect secrets from a table
  collectSecretsFromTable(table) {
    const rows = table.querySelectorAll('tbody tr')
    const secrets = []

    rows.forEach(row => {
      const nameInput = row.querySelector('input[name="secret_change[payload][][name]"]')
      const valueInput = row.querySelector('input[name="secret_change[payload][][value]"]')

      if (nameInput && valueInput && nameInput.value.trim() && valueInput.value.trim()) {
        secrets.push({
          name: nameInput.value.trim(),
          value: valueInput.value.trim()
        })
      }
    })

    return secrets
  }

  // Re-enable all inputs in both sections
  enableAllInputs() {
    const allInputs = this.element.querySelectorAll('input, select, textarea')
    allInputs.forEach(input => {
      input.disabled = false
    })
  }

  // Disable inputs in hidden sections to prevent form submission conflicts
  disableHiddenSectionInputs(operation) {
    if (operation === 'upserted') {
      // Disable inputs in the remove section (but not the shared selects)
      const removeSection = this.removeSectionTarget
      const removeInputs = removeSection.querySelectorAll('input:not([data-secret-changes-target="removeKeyInput"]), textarea')
      removeInputs.forEach(input => {
        input.disabled = true
      })
    } else if (operation === 'removed') {
      // Disable inputs in the add section (but not the shared selects)
      const addSection = this.addSectionTarget
      const addInputs = addSection.querySelectorAll('input, textarea')
      addInputs.forEach(input => {
        input.disabled = true
      })
    }
  }
}